import { useEffect, useState } from "react";
import { Twitch, Youtube, Eye, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const TwitchLive = () => {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/twitch/status`);
        if (r.ok && active) setStatus(await r.json());
      } catch {}
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  if (!status) return null;

  if (!status.is_live) {
    return (
      <section className="border-b border-white/10 px-6 py-8 md:px-12" data-testid="twitch-section">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15">
              <Twitch className="h-5 w-5 text-neon" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted2">{t.twitch.kicker}</p>
              <p className="font-display text-lg font-bold" data-testid="twitch-offline-label">{t.twitch.offline}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={status.channel_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="twitch-follow-link"
              className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-neon hover:text-neon"
            >
              {t.twitch.follow}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="https://www.youtube.com/@fabiocomelli"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="twitch-youtube-link"
              className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-neon hover:text-neon"
            >
              <Youtube className="h-4 w-4" />
              YouTube
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="border-b border-white/10 px-6 py-12 md:px-12" data-testid="twitch-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 md:grid-cols-12">
        <Reveal className="md:col-span-5">
          <a href={status.channel_url} target="_blank" rel="noopener noreferrer" className="group relative block overflow-hidden border border-white/15" data-testid="twitch-live-thumbnail">
            {status.thumbnail_url && (
              <img src={status.thumbnail_url} alt="Anteprima live Twitch" className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            )}
            <span className="absolute left-4 top-4 flex items-center gap-2 bg-black/70 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-white backdrop-blur">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative h-2.5 w-2.5 rounded-full bg-red-500" />
              </span>
              LIVE
            </span>
          </a>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-neon">{t.twitch.live}</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl" data-testid="twitch-live-title">
            {status.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-4 font-mono text-xs uppercase tracking-widest text-muted2">
            {status.game_name && <span data-testid="twitch-live-game">{status.game_name}</span>}
            {status.viewer_count != null && (
              <span className="flex items-center gap-1.5" data-testid="twitch-live-viewers">
                <Eye className="h-4 w-4 text-neon" />
                {status.viewer_count} {t.twitch.viewers}
              </span>
            )}
          </div>
          <a
            href={status.channel_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="twitch-watch-link"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform duration-300 hover:scale-105"
          >
            <Twitch className="h-4 w-4" />
            {t.twitch.watch}
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default TwitchLive;
